import { getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPLink, IPText, IPTooltipHost } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import {
  getFontIconColorPrimary,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapSmall,
  globalTextStylesBold,
  infoIcon,
} from 'globalStyles';
import { ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';

interface ILearnMoreProps {
  learnMoreLink?: string;
  text?: string;
  bold?: boolean;
}

export const LearnMore = (props: ILearnMoreProps) => {
  const { t } = useTranslation(['translation']);
  const theme = getTheme();

  if (!props.learnMoreLink) return null;

  //
  // Main render
  //
  return (
    <IPStack
      horizontal
      tokens={globalStackTokensGapExtraSmall}
      verticalAlign="center"
      styles={{ root: { paddingTop: 5 } }}
    >
      <IPFontIcon iconName="OpenInNewTab" style={{ color: theme.palette.themePrimary }} />
      <IPText styles={props.bold ? globalTextStylesBold : undefined}>
        <IPLink href={props.learnMoreLink} target="_blank" rel="noopener noreferrer">
          {props.text || t('translation:General.Notifications.LearnMore')}
        </IPLink>
      </IPText>
    </IPStack>
  );
};

interface IInfoProps {
  message: string | JSX.Element | undefined;
  learnMoreLink?: string;
}

export const Info = (props: IInfoProps): JSX.Element | null => {
  const appContext = useContext(AppContext);
  const theme = getTheme();

  const getMessage = (): JSX.Element => {
    if (typeof props.message === 'string') {
      return <IPText>{props.message}</IPText>;
    } else {
      return props.message as JSX.Element;
    }
  };

  const getMessageJSX = (): JSX.Element => {
    return (
      <IPStack>
        <IPStack.Item>{getMessage()}</IPStack.Item>
        <IPStack.Item>
          <LearnMore learnMoreLink={props.learnMoreLink} />
        </IPStack.Item>
      </IPStack>
    );
  };

  //
  // Main render
  //
  if (!props.message) return null;

  return (
    <IPTooltipHost
      content={getMessageJSX()}
      styles={{ root: { cursor: 'pointer' } }}
      calloutProps={{ beakWidth: 0, setInitialFocus: true }} //set initial focus to focus the leanr more link so keyboard users can open it by pressing enter
    >
      <IPFontIcon
        tabIndex={0}
        {...infoIcon}
        style={{ color: getFontIconColorPrimary(appContext.useDarkMode, theme) }}
      />
    </IPTooltipHost>
  );
};

interface ITextInfoProps {
  message?: string | JSX.Element;
  learnMoreLink?: string;
  children?: ReactNode;
}

const TextInfo = (props: ITextInfoProps) => {
  return (
    <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
      {props.children}
      <Info message={props.message} learnMoreLink={props.learnMoreLink} />
    </IPStack>
  );
};

export default TextInfo;
